import { useMemo } from 'react';

import { GoogleDriveAppSelectorIcon } from '../../../common/constants/3p-product-configs/google/qf-icon';
import { TeamsIcon } from '../../../common/constants/3p-product-configs/teams/icon';
import { RovoLogoFromSVG } from '../../../common/constants/icons';
import {
	type PrimaryProductKey,
	ProductKeys,
	useProductConfigs,
} from '../../../common/constants/products';
import { useAvailableProducts } from '../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../controllers/store/bootstrap/accessors';
import { useSelectedProduct } from '../../../controllers/store/search';

// TODO: Render dynamic 1P products, hardcoded for now for Team 25
// This should be synced with the FilterRowLoading component that hard codes filter buttons
const supported1PApps = (primaryProduct?: PrimaryProductKey) => {
	if (!primaryProduct) {
		return [];
	}

	return [{ productKey: ProductKeys.Confluence, redirectToFPS: false }];
};

// TODO: Render dynamic 3P products, hardcoded for now for Team 25
// This should be synced with the FilterRowLoading component that hard codes filter buttons
const supported3PApps = [ProductKeys.GoogleDrive, ProductKeys.Teams];

// TODO: Render the icons based on configuration, hardcoded for now for Team 25
const appSelectorIcons = {
	[ProductKeys.GoogleDrive]: RovoLogoFromSVG(GoogleDriveAppSelectorIcon),
	[ProductKeys.Teams]: RovoLogoFromSVG(TeamsIcon),
};

export const useFiltersRowAppSelector: () => {
    apps1PToDisplay: {
        productConfig: import("../../../common/constants/products").ProductConfig;
        productKey: "confluence";
        redirectToFPS: boolean;
    }[];
    apps3PToDisplay: {
        productKey: "drive" | "teams";
        productConfig: {
            Icon: import("../../../common/constants/icons").RovoLogo;
            displayName: import("react").ReactNode;
            IconBorderless?: import("../../../common/constants/icons").RovoLogo;
            is3P?: boolean;
            SearchResultIcon?: import("../../../common/constants/icons").RovoLogo;
        };
    }[];
    primaryProduct: PrimaryProductKey | undefined;
    selectedProduct: string;
} = () => {
	const [primaryProduct] = usePrimaryProduct();
	const availableProducts = useAvailableProducts();
	const [selectedProduct] = useSelectedProduct();
	const productConfigs = useProductConfigs();

	const apps3PToDisplay = useMemo(
		() =>
			supported3PApps
				.filter((supported3PApp) => availableProducts.includes(supported3PApp))
				.filter((_) => !selectedProduct)
				.map((productKey) => ({
					productKey,
					productConfig: productConfigs[productKey],
				}))
				// TODO: Remove the following Icon override of the icons when we render the icons based on a configuration
				.map(({ productKey, productConfig }) => ({
					productKey,
					productConfig: {
						...productConfig,
						...(appSelectorIcons[productKey] ? { Icon: appSelectorIcons[productKey] } : {}),
					},
				})),
		[availableProducts, productConfigs, selectedProduct],
	);

	const apps1PToDisplay = useMemo(
		() =>
			supported1PApps(primaryProduct)
				.filter((supported1PApp) => availableProducts.includes(supported1PApp.productKey))
				.filter(
					(supported1PApp) => !selectedProduct || supported1PApp.productKey === selectedProduct,
				)
				.map((supportedApp) => ({
					...supportedApp,
					productConfig: productConfigs[supportedApp.productKey],
				})),
		[availableProducts, primaryProduct, productConfigs, selectedProduct],
	);

	return {
		apps1PToDisplay,
		apps3PToDisplay,
		primaryProduct,
		selectedProduct,
	};
};
